import { assert } from "./errors";
import { getElementMeta } from "./schema";
export * from "./errors";
let nextUid = 0;
// eslint-disable-next-line @typescript-eslint/ban-types
const uidIndex = new WeakMap();
// eslint-disable-next-line @typescript-eslint/ban-types
export const getUid = (obj) => {
    let uid = uidIndex.get(obj);
    if (uid === undefined) {
        uid = nextUid;
        nextUid += 1;
        uidIndex.set(obj, uid);
    }
    return uid;
};
export const strPosToUni = (s, strOffset = s.length) => {
    let pairs = 0;
    let i = 0;
    for (; i < strOffset; i += 1) {
        const code = s.charCodeAt(i);
        if (code >= 0xd800 && code <= 0xdfff) {
            pairs += 1;
            i += 1; // Skip the second part of the pair.
        }
    }
    if (i !== strOffset)
        throw Error("Invalid offset - splits unicode bytes");
    return i - pairs;
};
export const uniToStrPos = (s, uniOffset) => {
    let pos = 0;
    for (; uniOffset > 0; uniOffset -= 1) {
        const code = s.charCodeAt(pos);
        pos += code >= 0xd800 && code <= 0xdfff ? 2 : 1;
    }
    return pos;
};
export const uniCount = (s) => strPosToUni(s);
export const uniSlice = (str, uniStart, uniEnd) => {
    return str.slice(uniToStrPos(str, uniStart), uniToStrPos(str, uniEnd));
};
export const DEFAULT_FORMAT = {
    bold: false,
    italic: false,
    underline: false,
    dashed: false,
    overline: false,
    lineThrough: false,
    color: null,
    backgroundColor: null,
};
const normalizeCustomFormat = (customs, baseCustoms) => {
    if (customs === null)
        return undefined;
    if (customs === undefined)
        return baseCustoms;
    const result = { ...baseCustoms, ...customs };
    for (const key of Object.keys(result)) {
        if (result[key] === undefined) {
            delete result[key];
        }
    }
    return result;
};
export const normalizeFormat = (format, baseFormat = DEFAULT_FORMAT) => {
    if (format == null)
        return baseFormat;
    return {
        ...baseFormat,
        ...format,
        customs: normalizeCustomFormat(format.customs, baseFormat.customs),
    };
};
const areCustomFormatsEqual = (format1, format2) => {
    var _a, _b;
    const customs1 = (_a = format1.customs) !== null && _a !== void 0 ? _a : {};
    const customs2 = (_b = format2.customs) !== null && _b !== void 0 ? _b : {};
    const keys1 = Object.keys(customs1);
    const keys2 = Object.keys(customs2);
    if (keys1.length !== keys2.length)
        return false;
    for (const key of new Set([...keys1, ...keys2])) {
        if (!Object.is(customs1[key], customs2[key])) {
            return false;
        }
    }
    return true;
};
export const areFormatsEqual = (format1, format2) => {
    if (format1.bold === format2.bold &&
        format1.italic === format2.italic &&
        format1.underline === format2.underline &&
        format1.dashed === format2.dashed &&
        format1.overline === format2.overline &&
        format1.lineThrough === format2.lineThrough &&
        format1.color === format2.color &&
        format1.backgroundColor === format2.backgroundColor &&
        areCustomFormatsEqual(format1, format2)) {
        return true;
    }
    return false;
};
class Selection {
    constructor(anchorOffset, focusOffset) {
        this.anchorOffset = anchorOffset;
        this.focusOffset = focusOffset;
    }
    get __brand() {
        return "2a02d637-ab0b-4f7e-99fb-6ebe7470ab1f";
    }
    get isCollapsed() {
        return this.anchorOffset === this.focusOffset;
    }
    get offset() {
        return Math.min(this.anchorOffset, this.focusOffset);
    }
    get endOffset() {
        return Math.max(this.anchorOffset, this.focusOffset);
    }
    get length() {
        return this.endOffset - this.offset;
    }
    get isBackward() {
        return this.anchorOffset > this.focusOffset;
    }
}
export const newSelection = (anchorOffset, focusOffset) => {
    return new Selection(anchorOffset, focusOffset);
};
export const areSelectionsEqual = (selection1, selection2, visually = false) => {
    if (selection1 === null && selection2 === null)
        return true;
    if (selection1 === null || selection2 === null)
        return false;
    if (visually) {
        return (selection1.offset === selection2.offset &&
            selection1.endOffset === selection2.endOffset);
    }
    else {
        return (selection1.anchorOffset === selection2.anchorOffset &&
            selection1.focusOffset === selection2.focusOffset);
    }
};
export const newText = (content, format = {}) => {
    return {
        type: "text",
        content,
        length: uniCount(content),
        format: normalizeFormat(format, DEFAULT_FORMAT),
    };
};
export const newElement = (tag, props, content = null) => {
    return {
        type: "element",
        tag,
        props,
        content,
    };
};
export const getItemLength = (item) => {
    var _a;
    switch (item.type) {
        case "text":
            return item.length;
        case "element": {
            if (item.content === null) {
                return 1;
            }
            const contentLength = (_a = item.content.length) !== null && _a !== void 0 ? _a : 0;
            return contentLength + (getElementMeta(item.tag).hasBoundary ? 2 : 0);
        }
    }
};
export const newContent = (items) => {
    const result = { items: [], length: 0 };
    for (const item of items) {
        const index = result.items.length;
        result.items.push(item);
        result.length += getItemLength(item);
        checkMerge(result, index);
    }
    return result;
};
// Invariant: offsetRelativeToItemStart < itemLength
export const findItemIndex = (content, offset) => {
    assert(offset >= 0 && offset <= content.length);
    let index = 0;
    const numItems = content.items.length;
    while (index < numItems) {
        const item = content.items[index];
        const itemLength = getItemLength(item);
        if (offset < itemLength) {
            return [index, offset];
        }
        offset -= itemLength;
        index += 1;
    }
    assert(offset === 0);
    return [index, offset];
};
export const isVoidElment = (element) => {
    return element.content === null;
};
export const isEmptyElementWithBoundary = (item) => {
    var _a;
    return (item.type === "element" &&
        item.content !== null &&
        item.content.length === 0 &&
        ((_a = getElementMeta(item.tag).hasBoundary) !== null && _a !== void 0 ? _a : false));
};
export const isJustAfterEmptyElementWithBoundary = (content, offset) => {
    if (content.length === 0) {
        assert(offset === 0);
        return false;
    }
    const [itemIndex, itemOffset] = findItemIndex(content, offset);
    if (itemOffset === 0 && itemIndex >= 1) {
        const item = content.items[itemIndex - 1];
        return isEmptyElementWithBoundary(item);
    }
    if (itemOffset > 0) {
        const item = content.items[itemIndex];
        if (item.type === "element" && item.content !== null) {
            const shift = getElementMeta(item.tag).hasBoundary ? -1 : 0;
            return isJustAfterEmptyElementWithBoundary(item.content, itemOffset + shift);
        }
    }
    return false;
};
export const isJustBeforeEmptyElementWithBoundary = (content, offset) => {
    if (content.length === 0) {
        assert(offset === 0);
        return false;
    }
    const [itemIndex, itemOffset] = findItemIndex(content, offset);
    if (itemOffset === 0) {
        const item = content.items[itemIndex];
        return isEmptyElementWithBoundary(item);
    }
    if (itemOffset > 0) {
        const item = content.items[itemIndex];
        if (item.type === "element" && item.content !== null) {
            const shift = getElementMeta(item.tag).hasBoundary ? -1 : 0;
            return isJustAfterEmptyElementWithBoundary(item.content, itemOffset + shift);
        }
    }
    return false;
};
export const isJustInsideEmptyElementWithBoundary = (content, offset) => {
    if (content.length === 0) {
        assert(offset === 0);
        return false;
    }
    const [itemIndex, itemOffset] = findItemIndex(content, offset);
    if (itemOffset > 0) {
        const item = content.items[itemIndex];
        if (item.type === "element" && item.content !== null) {
            if (itemOffset === 1) {
                return isEmptyElementWithBoundary(item);
            }
            const shift = getElementMeta(item.tag).hasBoundary ? -1 : 0;
            return isJustInsideEmptyElementWithBoundary(item.content, itemOffset + shift);
        }
    }
    return false;
};
//#region Delete
const deleteFromText = (text, offset, endOffset) => {
    text.content =
        uniSlice(text.content, 0, offset) +
            uniSlice(text.content, endOffset, text.length);
    text.length = text.length - (endOffset - offset);
};
const deleteFromElement = (element, offset, endOffset) => {
    assert(element.content !== null);
    if (getElementMeta(element.tag).hasBoundary) {
        deleteFromContent(element.content, Math.max(0, offset - 1), Math.min(endOffset - 1, element.content.length));
    }
    else {
        deleteFromContent(element.content, offset, endOffset);
    }
};
const deleteFromItem = (item, offset, endOffset) => {
    switch (item.type) {
        case "text":
            return deleteFromText(item, offset, endOffset);
        case "element":
            return deleteFromElement(item, offset, endOffset);
    }
};
export const deleteFromContent = (content, offset, endOffset) => {
    if (endOffset === offset) {
        return;
    }
    assert(offset < content.length);
    const [headIndex, headOffset] = findItemIndex(content, offset);
    const [tailIndex, tailOffset] = findItemIndex(content, endOffset);
    let negShift = 0;
    const deleteFromItemAndCountShift = (item, startOffset, endOffset) => {
        const prevLength = getItemLength(item);
        const expectedLength = prevLength - (endOffset - startOffset);
        deleteFromItem(item, startOffset, endOffset);
        const actualLength = getItemLength(item);
        negShift -= actualLength - expectedLength;
    };
    if (headIndex === tailIndex) {
        const headItem = content.items[headIndex];
        deleteFromItemAndCountShift(headItem, headOffset, tailOffset);
    }
    else {
        let deleteStartIndex = headIndex;
        const deleteEndIndex = tailIndex;
        if (headOffset > 0) {
            const headItem = content.items[headIndex];
            deleteFromItemAndCountShift(headItem, headOffset, getItemLength(headItem));
            deleteStartIndex += 1;
        }
        if (tailOffset > 0) {
            assert(tailIndex < content.items.length);
            const tailItem = content.items[tailIndex];
            deleteFromItemAndCountShift(tailItem, 0, tailOffset);
        }
        content.items.splice(deleteStartIndex, deleteEndIndex - deleteStartIndex);
    }
    content.length -= endOffset - offset + negShift;
};
//#endregion
const checkMerge = (content, index) => {
    if (!(index - 1 >= 0 && index < content.items.length))
        return;
    const left = content.items[index - 1];
    const right = content.items[index];
    if (left.type === "text" && right.type === "text") {
        if (areFormatsEqual(left.format, right.format)) {
            content.items.splice(index - 1, 2, {
                ...left,
                content: left.content + right.content,
                length: left.length + right.length,
            });
        }
    }
};
export const concatContents = (left, right) => {
    if (left.length === 0)
        return right;
    if (right.length === 0)
        return left;
    const concated = {
        items: [...left.items, ...right.items],
        length: left.length + right.length,
    };
    checkMerge(concated, left.items.length);
    return concated;
};
const splitText = (text, offset) => {
    assert(text.length > 0);
    const left = uniSlice(text.content, 0, offset);
    const right = uniSlice(text.content, offset, text.length);
    return [
        left.length === 0 ? null : { ...text, content: left, length: offset },
        right.length === 0
            ? null
            : {
                ...text,
                content: right,
                length: text.length - offset,
            },
    ];
};
const splitElement = (element, offset) => {
    if (element.content === null || element.content.length === 0) {
        return [element, null];
    }
    if (getElementMeta(element.tag).hasBoundary) {
        offset = Math.min(Math.max(0, offset - 1), element.content.length + 2);
    }
    const [left, right] = splitContent(element.content, offset);
    return [
        left.length === 0 ? null : { ...element, content: left },
        right.length === 0 ? null : { ...element, content: right },
    ];
};
const splitItem = (item, offset) => {
    if (item.type === "text") {
        return splitText(item, offset);
    }
    else if (item.type === "element") {
        return splitElement(item, offset);
    }
    else
        assert(false);
};
export const splitContent = (content, offset) => {
    const [itemIndex, itemOffset] = findItemIndex(content, offset);
    if (itemOffset === 0) {
        return [
            { items: content.items.slice(0, itemIndex), length: offset },
            {
                items: content.items.slice(itemIndex),
                length: content.length - offset,
            },
        ];
    }
    else {
        const [left, right] = splitItem(content.items[itemIndex], itemOffset);
        return [
            {
                items: left === null
                    ? content.items.slice(0, itemIndex)
                    : [...content.items.slice(0, itemIndex), left],
                length: offset,
            },
            {
                items: right === null
                    ? content.items.slice(itemIndex + 1)
                    : [right, ...content.items.slice(itemIndex + 1)],
                length: content.length - offset,
            },
        ];
    }
};
export const insertIntoContent = (content, offset, piece) => {
    if (piece.length === 0)
        return;
    if (content.length === 0) {
        assert(offset === 0);
        content.items = piece.items;
        content.length = piece.length;
        return;
    }
    const [itemIndex, itemOffset] = findItemIndex(content, offset);
    if (itemOffset > 0) {
        const item = content.items[itemIndex];
        if (item.type === "text") {
            const [left, right] = splitText(item, itemOffset);
            content.items.splice(itemIndex, 1, ...[
                ...(left !== null ? [left] : []),
                ...piece.items,
                ...(right !== null ? [right] : []),
            ]);
            content.length += piece.length;
            checkMerge(content, itemIndex + 1 + piece.items.length);
            checkMerge(content, itemIndex + 1);
        }
        else if (item.type === "element") {
            if (item.content !== null) {
                const isLegal = () => {
                    const elements = piece.items.filter((it) => it.type === "element");
                    return elements.every((it) => {
                        var _a, _b;
                        return (_b = (_a = getElementMeta(item.tag).allowContentElementTags) === null || _a === void 0 ? void 0 : _a.has(it.tag)) !== null && _b !== void 0 ? _b : false;
                    });
                };
                if (isLegal()) {
                    const oldLength = item.content.length;
                    insertIntoContent(item.content, itemOffset + (getElementMeta(item.tag).hasBoundary ? -1 : 0), piece);
                    content.length += item.content.length - oldLength;
                }
                else {
                    const oldLength = getItemLength(item);
                    const [left, right] = splitElement(item, itemOffset);
                    const newLength = (left !== null ? getItemLength(left) : 0) +
                        (right !== null ? getItemLength(right) : 0);
                    const shift = newLength - oldLength;
                    content.items.splice(itemIndex, 1, ...[
                        ...(left !== null ? [left] : []),
                        ...piece.items,
                        ...(right !== null ? [right] : []),
                    ]);
                    content.length += piece.length + shift;
                }
            }
            else
                assert(false);
        }
        else
            assert(false);
    }
    else {
        content.items.splice(itemIndex, 0, ...piece.items);
        content.length += piece.length;
        checkMerge(content, itemIndex + piece.length);
        checkMerge(content, itemIndex);
    }
};
export const formatContent = (content, startOffset, endOffset, format) => {
    if (content.length === 0 || endOffset === startOffset) {
        assert(startOffset >= 0 && endOffset <= content.length);
        return;
    }
    const [headIndex, headOffset] = findItemIndex(content, startOffset);
    assert(headIndex < content.items.length);
    const [tailIndex, tailOffset] = findItemIndex(content, endOffset);
    const formated = { items: [], length: NaN };
    const pushItem = (item) => {
        if (item.type === "text" && item.length === 0)
            return;
        const index = formated.items.length;
        formated.items.push(item);
        checkMerge(formated, index);
    };
    const formatItem = (item, startOffset, endOffset) => {
        if (item.type === "text") {
            pushItem({
                type: "text",
                content: uniSlice(item.content, 0, startOffset),
                length: startOffset,
                format: item.format,
            });
            pushItem({
                type: "text",
                content: uniSlice(item.content, startOffset, endOffset),
                length: endOffset - startOffset,
                format: normalizeFormat(format, item.format),
            });
            pushItem({
                type: "text",
                content: uniSlice(item.content, endOffset, item.length),
                length: item.length - endOffset,
                format: item.format,
            });
        }
        else {
            const meta = getElementMeta(item.tag);
            if (meta.setFormat) {
                meta.setFormat(item, format);
            }
            if (item.content !== null) {
                const shift = meta.hasBoundary ? -1 : 0;
                formatContent(item.content, Math.max(0, startOffset + shift), Math.min(item.content.length, endOffset + shift), format);
            }
            pushItem(item);
        }
    };
    if (headIndex === tailIndex) {
        assert(tailOffset > 0);
        formatItem(content.items[headIndex], headOffset, tailOffset);
    }
    else {
        if (headOffset > 0) {
            const headItem = content.items[headIndex];
            formatItem(headItem, headOffset, getItemLength(headItem));
        }
        const startIndex = headIndex + (headOffset > 0 ? 1 : 0);
        const endIndex = tailIndex;
        for (let index = startIndex; index < endIndex; index += 1) {
            const item = content.items[index];
            formatItem(item, 0, getItemLength(item));
        }
        if (tailOffset > 0) {
            const tailItem = content.items[tailIndex];
            formatItem(tailItem, 0, tailOffset);
        }
    }
    const formatStartIndex = headIndex;
    const formatEndIndex = tailIndex + (tailOffset > 0 ? 1 : 0);
    content.items.splice(formatStartIndex, formatEndIndex - formatStartIndex, ...formated.items);
    checkMerge(content, formatStartIndex + formated.items.length);
    checkMerge(content, formatStartIndex);
};
const sliceText = (text, startOffset, endOffset) => {
    return {
        ...text,
        content: uniSlice(text.content, startOffset, endOffset),
        length: endOffset - startOffset,
    };
};
const sliceElement = (element, startOffset, endOffset) => {
    assert(element.content !== null);
    const shift = getElementMeta(element.tag).hasBoundary ? -1 : 0;
    return {
        ...element,
        content: sliceContent(element.content, Math.max(0, startOffset + shift), Math.min(element.content.length, endOffset + shift)),
    };
};
const sliceItem = (item, startOffset, endOffset) => {
    assert(endOffset > startOffset);
    const itemLength = getItemLength(item);
    if (startOffset === 0 && endOffset === itemLength) {
        return item;
    }
    switch (item.type) {
        case "text":
            return sliceText(item, startOffset, endOffset);
        case "element":
            return sliceElement(item, startOffset, endOffset);
    }
};
export const sliceContent = (content, startOffset, endOffset) => {
    if (content.length === 0 || endOffset === startOffset) {
        assert(startOffset >= 0 && endOffset <= content.length);
        return newContent([]);
    }
    const [headIndex, headOffset] = findItemIndex(content, startOffset);
    const [tailIndex, tailOffset] = findItemIndex(content, endOffset);
    const result = { items: [], length: 0 };
    const pushItem = (item) => {
        const index = result.items.length;
        result.items.push(item);
        result.length += getItemLength(item);
        checkMerge(result, index);
    };
    if (headIndex === tailIndex && tailOffset > 0) {
        assert(headIndex < content.items.length);
        const item = content.items[headIndex];
        pushItem(sliceItem(item, headOffset, tailOffset));
    }
    else {
        let sliceStartIndex = headIndex;
        const sliceEndIndex = tailIndex;
        if (headOffset > 0) {
            const headItem = content.items[headIndex];
            pushItem(sliceItem(headItem, headOffset, getItemLength(headItem)));
            sliceStartIndex += 1;
        }
        if (tailIndex > headIndex) {
            for (const item of content.items.slice(sliceStartIndex, sliceEndIndex)) {
                pushItem(item);
            }
        }
        if (tailOffset > 0) {
            const tailItem = content.items[tailIndex];
            pushItem(sliceItem(tailItem, 0, tailOffset));
        }
    }
    return result;
};
const internalLegalizeContent = (content, allowTags) => {
    const result = { items: [], length: 0 };
    const pushItem = (item) => {
        const index = result.items.length;
        result.items.push(item);
        result.length += getItemLength(item);
        checkMerge(result, index);
    };
    const legalizeItem = (item) => {
        if (item.type === "text") {
            pushItem(item);
        }
        else if (item.type === "element") {
            if (allowTags.has(item.tag)) {
                pushItem(item);
            }
            else {
                const meta = getElementMeta(item.tag);
                const content = meta.legalize
                    ? meta.legalize(item, allowTags)
                    : item.content;
                if (content !== null && content.length > 0) {
                    const newContent = internalLegalizeContent(content, allowTags);
                    for (const item of newContent.items) {
                        pushItem(item);
                    }
                }
            }
        }
        else
            assert(false);
    };
    for (const item of content.items) {
        legalizeItem(item);
    }
    return result;
};
export const legalizeContent = (content, containerTag) => {
    var _a;
    return internalLegalizeContent(content, (_a = getElementMeta(containerTag).allowContentElementTags) !== null && _a !== void 0 ? _a : new Set());
};
const iterContentTexts = function* (content) {
    for (const item of content.items) {
        if (item.type === "element") {
            const meta = getElementMeta(item.tag);
            if (Object.prototype.hasOwnProperty.call(meta, "toString")) {
                yield meta.toString(item);
            }
            else if (item.content !== null) {
                yield* iterContentTexts(item.content);
            }
        }
        else {
            yield item.content;
        }
    }
};
export const contentToString = (content) => {
    let buffer = "";
    for (const text of iterContentTexts(content)) {
        buffer += text;
    }
    return buffer;
};
export const liftCursor = (content, offset, direction) => {
    const lift = (content, offset, baseOffset) => {
        const [itemIndex, itemOffset] = findItemIndex(content, offset);
        const itemBaseOffset = baseOffset +
            content.items
                .slice(0, itemIndex)
                .map(getItemLength)
                .reduce((a, b) => a + b, 0);
        if (itemOffset > 0) {
            const item = content.items[itemIndex];
            const itemLength = getItemLength(item);
            if (item.type === "element" && item.content !== null) {
                if (getElementMeta(item.tag).hasBoundary) {
                    if (direction === "backward" && itemOffset === 1) {
                        return itemBaseOffset;
                    }
                    if (direction === "forward" && itemOffset === itemLength - 1) {
                        return itemBaseOffset + itemLength;
                    }
                    const liftedOffset = lift(item.content, itemOffset - 1, itemBaseOffset + 1);
                    if (direction === "backward" && liftedOffset === itemBaseOffset + 1) {
                        return itemBaseOffset;
                    }
                    if (direction === "forward" &&
                        liftedOffset === itemBaseOffset + itemLength - 1) {
                        return itemBaseOffset + itemLength;
                    }
                }
                else {
                    return lift(item.content, itemOffset, itemBaseOffset);
                }
            }
        }
        return itemBaseOffset + itemOffset;
    };
    return lift(content, offset, 0);
};
export const dropCursor = (content, offset, direction) => {
    const drop = (content, offset, baseOffset) => {
        const [itemIndex, itemOffset] = findItemIndex(content, offset);
        const itemBaseOffset = baseOffset +
            content.items
                .slice(0, itemIndex)
                .map(getItemLength)
                .reduce((a, b) => a + b, 0);
        if (itemOffset > 0) {
            const item = content.items[itemIndex];
            assert(itemOffset < getItemLength(item));
            if (item.type === "element" && item.content !== null) {
                const shift = getElementMeta(item.tag).hasBoundary ? -1 : 0;
                return drop(item.content, itemOffset + shift, itemBaseOffset);
            }
        }
        else {
            if (direction === "backward" && itemIndex >= 1) {
                const item = content.items[itemIndex - 1];
                const itemLength = getItemLength(item);
                if (item.type === "element" && item.content !== null) {
                    const shift = getElementMeta(item.tag).hasBoundary ? -1 : 0;
                    return drop(item.content, itemLength + shift, itemBaseOffset);
                }
            }
            else if (direction === "forward" && itemIndex < content.items.length) {
                const item = content.items[itemIndex];
                if (item.type === "element" && item.content !== null) {
                    const shift = getElementMeta(item.tag).hasBoundary ? -1 : 0;
                    return drop(item.content, itemOffset + shift, itemBaseOffset);
                }
            }
        }
        return itemBaseOffset + offset;
    };
    return drop(content, offset, 0);
};
export const isOffsetInItemRange = (offset, item, baseOffset) => {
    if (item.type === "text") {
        return offset >= baseOffset && offset <= baseOffset + item.length;
    }
    else if (item.type === "element") {
        if (item.content === null) {
            return offset >= baseOffset && offset <= baseOffset + 1;
        }
        else {
            const contentStartOffset = baseOffset + (getElementMeta(item.tag).hasBoundary ? 1 : 0);
            const contentEndOffset = contentStartOffset + item.content.length;
            return offset >= contentStartOffset && offset <= contentEndOffset;
        }
    }
    else
        assert(false);
};
export const iterOffsetAndPathOfContentItems = function* (content, baseOffset, path) {
    let offset = baseOffset;
    for (let itemIndex = 0; itemIndex < content.items.length; itemIndex += 1) {
        const item = content.items[itemIndex];
        const endOffset = offset + getItemLength(item);
        const sent = yield { item, offset, path: [...path, itemIndex] };
        const down = sent !== null && sent !== void 0 ? sent : true;
        if (down && item.type === "element" && item.content !== null) {
            yield* iterOffsetAndPathOfContentItems(item.content, offset + (getElementMeta(item.tag).hasBoundary ? 1 : 0), [...path, itemIndex]);
        }
        offset = endOffset;
    }
};
export const findItemPathInRange = (content, startOffset, endOffset, predicate) => {
    const gen = iterOffsetAndPathOfContentItems(content, 0, []);
    let r = gen.next();
    // eslint-disable-next-line no-constant-condition
    while (true) {
        if (r.done)
            return [];
        const { item, offset, path } = r.value;
        const itemLength = getItemLength(item);
        if (endOffset >= offset && startOffset <= offset + itemLength) {
            if (predicate(item, offset)) {
                return path;
            }
            else {
                r = gen.next(true);
                continue;
            }
        }
        if (endOffset < offset + itemLength) {
            return [];
        }
        r = gen.next(false);
    }
};
export const findContainerItemPath = (content, startOffset, endOffset, predicate) => {
    const gen = iterOffsetAndPathOfContentItems(content, 0, []);
    let r = gen.next();
    // eslint-disable-next-line no-constant-condition
    while (true) {
        if (r.done)
            return [];
        const { item, offset, path } = r.value;
        const itemLength = getItemLength(item);
        if ((startOffset === offset && endOffset === offset + itemLength) ||
            (isOffsetInItemRange(startOffset, item, offset) &&
                isOffsetInItemRange(endOffset, item, offset))) {
            if (predicate(item, offset)) {
                return path;
            }
            else {
                r = gen.next(true);
                continue;
            }
        }
        if (offset > startOffset) {
            return [];
        }
        r = gen.next(false);
    }
};
export const getElementByPath = (content, path) => {
    if (path.length === 0)
        return null;
    const first = path[0];
    const rest = path.slice(1);
    const element = content.items[first];
    if (rest.length === 0) {
        return element;
    }
    return getElementByPath(element.content, rest);
};
export const getElementRangeByPath = (content, path) => {
    const aux = (content, path, baseOffset) => {
        assert(path.length > 0);
        const itemIndex = path[0];
        const childPath = path.slice(1);
        const item = content.items[itemIndex];
        assert(item.type === "element");
        const itemBaseOffset = baseOffset +
            content.items
                .slice(0, itemIndex)
                .map(getItemLength)
                .reduce((a, b) => a + b, 0);
        if (childPath.length > 0) {
            assert(item.content !== null);
            return aux(item.content, childPath, itemBaseOffset);
        }
        else {
            return [itemBaseOffset, itemBaseOffset + getItemLength(item)];
        }
    };
    return aux(content, path, 0);
};
export const wrapContent = (content, offset, endOffset, tag, props) => {
    let piece = sliceContent(content, offset, endOffset);
    piece = legalizeContent(piece, tag);
    piece = newContent([{ type: "element", tag, props, content: piece }]);
    deleteFromContent(content, offset, endOffset);
    insertIntoContent(content, offset, piece);
};
export const unwrapContent = (content, path) => {
    const aux = (content, path) => {
        assert(path.length > 0);
        const childPath = path.slice(1);
        const itemIndex = path[0];
        const item = content.items[itemIndex];
        assert(item.type === "element" && item.content !== null);
        if (childPath.length > 0) {
            const oldLength = item.content.length;
            aux(item.content, childPath);
            content.length += item.content.length - oldLength;
        }
        else {
            content.items.splice(itemIndex, 1, ...item.content.items);
            checkMerge(content, itemIndex + item.content.items.length);
            checkMerge(content, itemIndex);
            if (getElementMeta(item.tag).hasBoundary) {
                content.length -= 2;
            }
        }
    };
    return aux(content, path);
};
export const readFormatAtOffset = (content, offset) => {
    const findText = (content, offset) => {
        for (const item of content.items) {
            const itemLength = getItemLength(item);
            if (offset <= itemLength) {
                if (item.type === "text") {
                    return item;
                }
                else if (item.type === "element") {
                    if (item.content === null)
                        return null;
                    if (getElementMeta(item.tag).hasBoundary &&
                        (offset === 0 || offset === itemLength)) {
                        return null;
                    }
                    const shift = getElementMeta(item.tag).hasBoundary ? -1 : 0;
                    return findText(item.content, offset + shift);
                }
                else
                    assert(false);
            }
            offset -= itemLength;
        }
        return null;
    };
    const text = findText(content, offset);
    if (text !== null) {
        return text.format;
    }
    return DEFAULT_FORMAT;
};
export const sliceMarkers = (markers, startOffset, endOffset) => {
    return markers.filter((marker) => {
        if (marker.endOffset <= startOffset)
            return false;
        if (marker.offset >= endOffset)
            return false;
        return true;
    });
};
// CREDIT: copied and modified from slatejs
export const decorations = (text, baseOffset, markers) => {
    let result = [[text, []]];
    for (const marker of markers) {
        const next = [];
        let o = baseOffset;
        for (const item of result) {
            const length = item[0].length;
            const offset = o;
            o += length;
            const start = marker.offset;
            const end = marker.endOffset;
            // If the range encompases the entire leaf, add the range.
            if (start <= offset && end >= o) {
                item[1].push(marker);
                next.push(item);
                continue;
            }
            // If the range expanded and match the leaf, or starts after, or ends before it, continue.
            if ((start !== end && (start === o || end === offset)) ||
                start > o ||
                end < offset ||
                (end === offset && offset !== 0)) {
                next.push(item);
                continue;
            }
            // Otherwise we need to split the leaf, at the start, end, or both,
            // and add the range to the middle intersecting section. Do the end
            // split first since we don't need to update the offset that way.
            let middle = item;
            let before;
            let after;
            if (end < o) {
                const off = end - offset;
                after = [sliceText(middle[0], off, middle[0].length), [...middle[1]]];
                middle = [sliceText(middle[0], 0, off), [...middle[1]]];
            }
            if (start > offset) {
                const off = start - offset;
                before = [sliceText(middle[0], 0, off), [...middle[1]]];
                middle = [sliceText(middle[0], off, middle[0].length), [...middle[1]]];
            }
            middle[1].push(marker);
            if (before) {
                next.push(before);
            }
            next.push(middle);
            if (after) {
                next.push(after);
            }
        }
        result = next;
    }
    return result;
};
